import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { departementCourant, peutModifier } from '../services/droits';
import { stageAPI } from '../services/api';
import StageModale from './StageModale';
import StageModaleEnvoi, { copier } from './StageModaleEnvoi';
import StageFenetrePromotion from './StageFenetrePromotion';
import StagePanneauDossier from './StagePanneauDossier';

/**
 * SUIVI DE STAGE — l'annuaire des entreprises (lot 3.7a)
 * =======================================================
 * Spec     : doc/modules/suivi_de_stage.md § 3 — Annuaire entreprises, écarts E125 → E129
 * Maquette : #tab-entreprises, revue par Bertrand au cadrage du 2026-10-01 :
 *   « l'idée de cet onglet est d'avoir un listing des entreprises présentes dans la base,
 *     des coordonnées pour permettre au responsable de stage de les retrouver ».
 *
 * ── UNE LIGNE PAR ENTREPRISE (E125) ────────────────────────────────────────
 * Entreprise et adresse · ses contacts · ses stagiaires et apprentis (plusieurs, sur
 * plusieurs années) · une case à cocher et l'enveloppe. ❌ Retirés de la maquette : la
 * colonne « Fiche reçue », la ligne grisée « fiche non encore reçue », l'œil, l'export.
 *
 * ── LE SEUL GESTE : ÉCRIRE (E128, revu au lot 3.5d-0 — E164) ─────────────────
 * La RÈGLE COMMUNE des courriels : ✉ sur une ligne → la fenêtre d'envoi individuelle (À = ses contacts) ;
 * les cases cochées → la fenêtre GROUPÉE, UN MESSAGE PAR ENTREPRISE (son premier contact, les autres en
 * copie). Message LIBRE (objet et texte vides), hors modèles ; journal « Message aux entreprises ».
 * Sans serveur d'envoi, ou en lecture seule : le `mailto:` d'avant (copie cachée) reste le secours.
 * ⚠️ Trop d'adresses pour un `mailto:` (2 000 caractères, mesuré — E33) : même solution
 * que le lien de promotion, les adresses sont COPIÉES et restent visibles. Pas de
 * brouillon téléchargeable : refusé trois fois par Bertrand le 2026-09-25.
 */

/** La limite d'une adresse `mailto:` — la même que `LONGUEUR_MAILTO_MAX` côté serveur (E33). */
const LONGUEUR_MAILTO_MAX = 2000;
const normaliser = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const LIBELLE_REGIME = { initiale: 'Initiale', apprentis: 'Apprentissage', fc: 'Formation continue' };
const CLASSE_REGIME = { initiale: 'badge-wait', apprentis: 'badge-ok', fc: 'annu-fc' };
const adressesDe = (entreprises) => [...new Set(entreprises.flatMap((e) => e.contacts.map((c) => c.email).filter(Boolean)))];

/**
 * Le filtre des régimes : un menu déroulant à CASES À COCHER (demandé par Bertrand).
 * Tout coché = aucun filtre. Se referme au clic dehors ou sur Échap.
 */
function MenuRegimes({ regimes, choisis, onChange }) {
    const [ouvert, setOuvert] = useState(false);
    const racine = useRef(null);
    useEffect(() => {
        if (!ouvert) return undefined;
        const dehors = (e) => { if (racine.current && !racine.current.contains(e.target)) setOuvert(false); };
        const echap = (e) => { if (e.key === 'Escape') setOuvert(false); };
        document.addEventListener('mousedown', dehors);
        document.addEventListener('keydown', echap);
        return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
    }, [ouvert]);
    const resume = choisis.length === regimes.length ? 'Tous les régimes'
        : choisis.length === 0 ? 'Aucun régime'
        : regimes.filter((r) => choisis.includes(r.code)).map((r) => r.libelle).join(', ');
    return (
        <div className="annu-menu" ref={racine}>
            <button type="button" className="input-sm annu-menu-bouton" aria-haspopup="true" aria-expanded={ouvert}
                    aria-label="Filtrer par régime" onClick={() => setOuvert(!ouvert)}>
                <span>{resume}</span> <i className={'fa-solid ' + (ouvert ? 'fa-chevron-up' : 'fa-chevron-down')} />
            </button>
            {ouvert && (
                <div className="annu-menu-liste" role="group" aria-label="Régimes">
                    {regimes.map((r) => (
                        <label key={r.code}>
                            <input type="checkbox" checked={choisis.includes(r.code)}
                                   onChange={(e) => onChange(e.target.checked ? [...choisis, r.code] : choisis.filter((x) => x !== r.code))} />
                            {r.libelle}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function StageAnnuaire({ config, user }) {
    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'stage');
    const [donnees, setDonnees] = useState(null);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState(null);
    const [recherche, setRecherche] = useState('');
    const [formation, setFormation] = useState('');
    const [annee, setAnnee] = useState('');
    const [regimes, setRegimes] = useState(['initiale', 'apprentis', 'fc']);
    const [coches, setCoches] = useState(() => new Set());
    const [groupe, setGroupe] = useState(null);       // la modale du message groupé
    const [copie, setCopie] = useState(null);
    const [dossierOuvert, setDossierOuvert] = useState(null);
    const [envoi, setEnvoi] = useState(null);
    const [groupeServeur, setGroupeServeur] = useState(null);   // lot 3.5d-0 : la fenêtre groupée (un message par entreprise)
    const [versionPanneau, setVersionPanneau] = useState(0);

    // ⚠️ Aucun rappel de parent dans ces dépendances (leçon du lot 1.3 bis).
    const recharger = useCallback(async () => {
        if (!departementId) return;
        setChargement(true); setErreur(null);
        try {
            const r = await stageAPI.getAnnuaire(departementId);
            setDonnees(r.data);
        } catch (e) {
            setErreur(e.response?.data?.detail || e.response?.data?.error || e.message || "Lecture de l'annuaire impossible");
            setDonnees(null);
        } finally { setChargement(false); }
    }, [departementId]);
    useEffect(() => { recharger(); }, [recharger]);

    /**
     * Les entreprises VISIBLES : celles qui ont au moins un étudiant dans les filtres
     * (promotion, année, régime) — et, sur chaque ligne, seulement ces étudiants-là.
     * La recherche porte sur l'entreprise, sa ville, ses contacts et ses étudiants.
     */
    const visibles = useMemo(() => {
        const q = normaliser(recherche.trim());
        return (donnees?.entreprises || []).map((e) => {
            const etudiants = e.etudiants.filter((x) => (!formation || String(x.sous_niveau_id) === formation)
                && (!annee || String(x.annee_id) === annee) && regimes.includes(x.regime));
            if (!etudiants.length) return null;
            if (q) {
                const foin = normaliser([e.nom, e.ville, e.cp, ...e.contacts.flatMap((c) => [c.nom, c.prenom, c.email, c.fonction]),
                    ...e.etudiants.flatMap((x) => [x.nom, x.prenom])].join(' '));
                if (!foin.includes(q)) return null;
            }
            return { ...e, etudiants };
        }).filter(Boolean);
    }, [donnees, recherche, formation, annee, regimes]);

    // Le message groupé ne vise que les cases cochées ENCORE VISIBLES : une entreprise
    // masquée par un filtre ne reçoit rien sans qu'on le voie.
    const cochees = visibles.filter((e) => coches.has(e.cle));
    const cocherable = visibles.filter((e) => e.contacts.some((c) => c.email));
    const toutCoche = cocherable.length > 0 && cocherable.every((e) => coches.has(e.cle));
    const basculer = (k) => setCoches((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
    const toutBasculer = () => setCoches((s) => {
        const n = new Set(s);
        cocherable.forEach((e) => (toutCoche ? n.delete(e.cle) : n.add(e.cle)));
        return n;
    });

    /**
     * Lot 3.5d-0 (E164) : les entreprises cochées → la fenêtre GROUPÉE (un message par entreprise). Sans serveur
     * d'envoi ou en lecture seule : le secours d'avant — un message en Cci (adresses copiées s'il ne tient pas).
     */
    const preparerGroupe = async () => {
        if (modifiable) {
            setErreur(null);
            try {
                const r = await stageAPI.preparerGroupeEntreprises(departementId, cochees.map((e) => e.cle));
                if (r.data.mode !== 'mailto') { setGroupeServeur(r.data); return; }
            } catch (e) {
                setErreur([e.response?.data?.error, typeof e.response?.data?.detail === 'string' ? e.response.data.detail : null].filter(Boolean).join(' — ') || "L'envoi n'a pas pu être préparé");
                return;
            }
        }
        preparerSecours();
    };
    /**
     * ✉ d'une ligne (lot 3.5d-0, E164) : la fenêtre d'envoi INDIVIDUELLE, objet et texte vides. Sans serveur
     * d'envoi ou en lecture seule, le lien `mailto:` de la ligne garde la main (secours d'avant).
     */
    const ecrireA = async (ev, e) => {
        const href = ev.currentTarget.getAttribute('href');
        if (!href) { ev.preventDefault(); return; }
        if (!modifiable) return;
        ev.preventDefault();
        setErreur(null);
        try {
            const r = await stageAPI.preparerEnvoiEntreprise(departementId, e.cle);
            if (r.data.mode === 'mailto') window.location.href = href;
            else setEnvoi(r.data);
        } catch (err) {
            setErreur([err.response?.data?.error, typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null].filter(Boolean).join(' — ') || "Le message n'a pas pu être préparé");
        }
    };
    /** Le secours : un message en Cci ; s'il ne tient pas dans un `mailto:`, les adresses sont copiées. */
    const preparerSecours = async () => {
        const adresses = adressesDe(cochees);
        const mailto = 'mailto:?bcc=' + encodeURIComponent(adresses.join(','));
        const trop = mailto.length > LONGUEUR_MAILTO_MAX;
        setCopie(null);
        setGroupe({ adresses, nbEntreprises: cochees.length, trop, mailto: trop ? 'mailto:' : mailto });
        if (trop) setCopie(await copier(adresses.join('; ')));
    };

    return (
        <div>
            <div className="stg-encart stg-encart-info">
                <i className="fa-solid fa-circle-info" />
                <span>Annuaire des entreprises — coordonnées collectées par les <b>fiches de liaison</b> des stagiaires et le <b>registre Alternance</b>. Toutes les années.</span>
            </div>

            <div className="stg-filtres">
                <div className="stg-recherche">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input placeholder="Entreprise, contact, étudiant…" value={recherche}
                           aria-label="Rechercher une entreprise, un contact, un étudiant" onChange={(e) => setRecherche(e.target.value)} />
                </div>
                <select className="input-sm stg-filtre" value={formation} aria-label="Promotion" onChange={(e) => setFormation(e.target.value)}>
                    <option value="">Toutes les promotions</option>
                    {(donnees?.formations || []).map((f) => <option key={f.id} value={String(f.id)}>{f.nom}</option>)}
                </select>
                <select className="input-sm stg-filtre" value={annee} aria-label="Année" onChange={(e) => setAnnee(e.target.value)}>
                    <option value="">Toutes les années</option>
                    {(donnees?.annees || []).map((a) => <option key={a.id} value={String(a.id)}>{a.libelle}</option>)}
                </select>
                <MenuRegimes regimes={donnees?.regimes || []} choisis={regimes} onChange={setRegimes} />
                <div style={{ marginLeft: 'auto' }}>
                    <button type="button" className="btn btn-green btn-sm" disabled={!cochees.length}
                            title={cochees.length ? 'Un message par entreprise, à ses contacts' : 'Cochez une ou plusieurs entreprises'}
                            onClick={preparerGroupe}>
                        <i className="fa-solid fa-envelope" /> Écrire aux entreprises cochées ({cochees.length})
                    </button>
                </div>
            </div>

            {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}

            <div className="card">
                <div className="card-header">
                    <span>
                        <i className="fa-solid fa-building" style={{ color: 'var(--stg-vert-d)' }} />{' '}
                        {chargement && !donnees ? 'Lecture…' : `${visibles.length} entreprise${visibles.length > 1 ? 's' : ''}`}
                        {cochees.length > 0 && <span style={{ fontWeight: 400, color: 'var(--stg-muted)' }}> · {cochees.length} cochée{cochees.length > 1 ? 's' : ''}</span>}
                    </span>
                </div>
                <div className="stg-table-defile">
                    <table className="annu-table">
                        <thead>
                            <tr>
                                <th style={{ width: 32 }}>
                                    <input type="checkbox" checked={toutCoche} disabled={!cocherable.length}
                                           aria-label="Cocher toutes les entreprises affichées" onChange={toutBasculer} />
                                </th>
                                <th>Entreprise</th>
                                <th>Contacts</th>
                                <th>Stagiaires et apprentis</th>
                                <th style={{ width: 40 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {!chargement && !visibles.length && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--stg-muted)' }}>Aucune entreprise ne correspond.</td></tr>}
                            {visibles.map((e) => {
                                const emails = e.contacts.map((c) => c.email).filter(Boolean);
                                return (
                                    <tr key={e.cle} className={coches.has(e.cle) ? 'annu-cochee' : undefined}>
                                        <td>
                                            <input type="checkbox" checked={coches.has(e.cle)} disabled={!emails.length}
                                                   title={emails.length ? undefined : 'Aucune adresse courriel connue pour cette entreprise'}
                                                   aria-label={`Cocher ${e.nom}`} onChange={() => basculer(e.cle)} />
                                        </td>
                                        <td>
                                            <strong>{e.nom}</strong>
                                            {(e.adresse || e.cp || e.ville) && (
                                                <div className="annu-gris">{[e.adresse, [e.cp, e.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</div>
                                            )}
                                        </td>
                                        <td>
                                            {!e.contacts.length && <span className="annu-gris">—</span>}
                                            {e.contacts.map((c, i) => (
                                                <div className="annu-contact" key={i}>
                                                    <div>{[c.civilite, c.prenom, c.nom].filter(Boolean).join(' ')}{c.fonction && <span className="annu-gris"> · {c.fonction}</span>}</div>
                                                    {c.email && <div className="annu-petit">{c.email}</div>}
                                                    {c.telephone && <div className="annu-petit annu-gris">{c.telephone}</div>}
                                                </div>
                                            ))}
                                        </td>
                                        <td>
                                            {e.etudiants.map((x) => (
                                                <div className="annu-etudiant" key={`${x.source}-${x.etudiant_id}-${x.annee_id}`}>
                                                    {x.dossier_id ? (
                                                        <button type="button" className="stg-lien annu-nom" title="Ouvrir son dossier de stage"
                                                                onClick={() => setDossierOuvert(x.dossier_id)}>{x.nom} {x.prenom}</button>
                                                    ) : <span className="annu-nom">{x.nom} {x.prenom}</span>}
                                                    <span className="annu-gris"> · {x.formation_nom || '—'} · {x.annee_libelle}</span>
                                                    {' '}<span className={'badge ' + (CLASSE_REGIME[x.regime] || 'badge-wait')}>{LIBELLE_REGIME[x.regime] || x.regime}</span>
                                                </div>
                                            ))}
                                        </td>
                                        <td className="actions">
                                            <a className={'action-btn' + (emails.length ? '' : ' annu-inactif')} aria-disabled={!emails.length}
                                               title={emails.length ? `Écrire à ${e.nom}` : 'Aucune adresse courriel connue'}
                                               href={emails.length ? 'mailto:' + emails.map(encodeURIComponent).join(',').replace(/%40/g, '@') : undefined}
                                               onClick={(ev) => ecrireA(ev, e)}>
                                                <i className="fa-solid fa-envelope" />
                                            </a>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {groupe && (
                <StageModale titre={`Écrire à ${groupe.nbEntreprises} entreprise${groupe.nbEntreprises > 1 ? 's' : ''}`} icone="fa-envelope"
                             libelleValider="Ouvrir le message" libelleAnnuler="Fermer"
                             onValider={() => { window.location.href = groupe.mailto; }} onFermer={() => setGroupe(null)}>
                    <p style={{ fontSize: 12 }}>
                        <b>{groupe.adresses.length}</b> adresse{groupe.adresses.length > 1 ? 's' : ''}, en <b>copie cachée (Cci)</b> :
                        aucune entreprise ne verra les adresses des autres.
                    </p>
                    {groupe.trop && (
                        <div className="stg-encart stg-encart-alerte">
                            <i className="fa-solid fa-clipboard" />
                            <span>Trop d'adresses pour les mettre directement dans le message : elles {copie === true ? 'sont déjà copiées' : 'sont à copier'}.
                                Ouvrez le message, puis <b>collez-les en Cci</b>.</span>
                        </div>
                    )}
                    <textarea className="input-sm annu-adresses" readOnly value={groupe.adresses.join('; ')} aria-label="Adresses" />
                    {groupe.trop && (
                        <button type="button" className="btn btn-outline btn-sm" onClick={async () => setCopie(await copier(groupe.adresses.join('; ')))}>
                            <i className="fa-solid fa-copy" /> Recopier les adresses
                        </button>
                    )}
                </StageModale>
            )}

            {groupeServeur && (
                <StageFenetrePromotion brouillon={groupeServeur} departementId={departementId}
                    api={{
                        lancer: (dep, corps) => stageAPI.lancerGroupeEntreprises(dep, { ...corps, cles: groupeServeur.contexte.cles }),
                        etat: (dep, id) => stageAPI.etatGroupeEntreprises(dep, id)
                    }}
                    onFermer={() => setGroupeServeur(null)} onTermine={() => {}} />
            )}
            {envoi && <StageModaleEnvoi envoi={envoi} departementId={departementId} onFermer={() => setEnvoi(null)}
                                        onTermine={() => { recharger(); setVersionPanneau((v) => v + 1); }} />}
            {dossierOuvert && (
                <StagePanneauDossier dossierId={dossierOuvert} departementId={departementId} user={user} modifiable={modifiable}
                                     onFermer={() => setDossierOuvert(null)} onChange={recharger}
                                     onMessage={(message) => { if (message && (message.mailto || message.mode === 'smtp')) setEnvoi(message); }}
                                     version={versionPanneau} />
            )}
        </div>
    );
}

export default StageAnnuaire;
